// Control/CopyField (Figma 14:36) — a monospace value plus a copy action.
// Screen-specific composite, not a `src/ui/` primitive: only the install
// screen uses it, same reasoning `screens/commerce` applies to `PlanCard`.
//
// Deliberately dumb: it copies exactly the `value` string it is given and
// reports nothing about what that value means. The caller (InstallScreen)
// is the one that knows whether `value` is a real key, a real snippet, or a
// placeholder — this component never decides that.

import { useState } from "react";
import { Icon } from "../../ui";
import "./ops.css";

export type CopyFieldProps = {
  value: string;
  label: string;
  /** Multi-line, rendered in a `<pre>` inside an inset panel — the embed
   *  snippet. Single-line otherwise — the key reveal. */
  block?: boolean;
  onCopied?: () => void;
};

export function CopyField({ value, label, block = false, onCopied }: CopyFieldProps) {
  const [failed, setFailed] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setFailed(false);
      onCopied?.();
    } catch {
      // Clipboard permission denied or unavailable (e.g. non-HTTPS, jsdom).
      // The value is still selectable text — fail visibly, not silently.
      setFailed(true);
    }
  }

  return (
    <div
      className={block ? "maisight-ops-codeblock" : "maisight-ops-copyfield"}
      data-chassis="paper"
    >
      {block ? (
        <div className="maisight-ops-codeblock-head">
          <span className="type-labelSm maisight-ops-codeblock-label">{label}</span>
          <button
            type="button"
            className="maisight-ops-copy-button"
            onClick={() => void copy()}
            aria-label={`Copy ${label.toLowerCase()}`}
          >
            <Icon name="copy" size={16} />
          </button>
        </div>
      ) : null}
      {block ? (
        <pre className="maisight-ops-codeblock-body type-code">{value}</pre>
      ) : (
        <>
          <span className="maisight-ops-copyfield-value type-code">{value}</span>
          <button
            type="button"
            className="maisight-ops-copy-button"
            onClick={() => void copy()}
            aria-label={`Copy ${label.toLowerCase()}`}
          >
            <Icon name="copy" size={16} />
          </button>
        </>
      )}
      {failed ? (
        <p className="maisight-ops-copyfield-failed type-bodyS" role="alert">
          Couldn't copy automatically — select the text and copy it manually.
        </p>
      ) : null}
    </div>
  );
}
